'use client';

import { ModalId } from '@heidi/schema/ml-scribe';

import { useQueuedModal } from '@/hooks/useQueuedModal';
import useWindowDimensions from '@/hooks/useWindowDimensions';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { ClipboardList } from 'lucide-react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

/**
 * One-time introduction modal for the precharting beta (Athena / Google ADK
 * agents).
 *
 * Visibility is owned by the modal queue (`ModalId.PRECHART_BETA_INTRO_MODAL`):
 * eligibility (`release-prechart` on) is computed in `launchModalsConfigAtom`,
 * and `seen_modals` keeps it to once per user across devices. Dismissal stamps
 * the seen state, which the companion `PrechartBetaTabSpotlight` waits on before
 * surfacing itself on the prechart tab.
 */
export const PrechartBetaIntroModal = () => {
  const { isOpen, handleClose, dismissible } = useQueuedModal(
    ModalId.PRECHART_BETA_INTRO_MODAL
  );
  const { isDesktop } = useWindowDimensions();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose]
  );

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="p-6 sm:max-w-sm"
        dismissible={dismissible}
        hideCloseIcon={!isDesktop}
        data-testid="prechart-beta-intro-modal"
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <FramedIcon icon={<ClipboardList />} />
          <div className="flex flex-col items-center gap-2">
            <DialogDrawerTitle className="text-balance">
              <FormattedMessage
                defaultMessage="Precharting is now in beta"
                id="zdBwLNQ/Lt"
              />
            </DialogDrawerTitle>
            <DialogDrawerDescription className="text-pretty">
              <FormattedMessage
                defaultMessage="Heidi can prepare a prechart from your patient's history before the visit, so you can walk in ready. This is an early beta, so it will keep getting better."
                id="gEzjVrYZ4j"
              />
            </DialogDrawerDescription>
          </div>
          <Button
            size="lg"
            onClick={handleClose}
            className="w-full"
            data-testid="prechart-beta-intro-cta"
          >
            <FormattedMessage defaultMessage="Got it" id="NYTGIbSNz5" />
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
